import { useState, useEffect, useCallback } from 'react';
import ConsentDisclaimer from '../../components/common/ConsentDisclaimer';
import { api } from '../../services/api';
import styles from './TelehealthConsentPage.module.css';

const TelehealthConsentPage = () => {
  const [consentState, setConsentState] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleConsentChange = useCallback((state) => {
    setConsentState(state);
  }, []);

  const handleSubmit = async () => {
    const allChecked = consentState?.allChecked;
    if (!allChecked || submitting) return;

    setSubmitting(true);

    const timestamp = new Date().toISOString();
    const consentData = {
      checkboxes: {
        consentPlatform: consentState.platform,
        consentProviderResponsibility: consentState.providerResponsibility,
        consentPrescriptionNotGuaranteed: consentState.prescriptionNotGuaranteed,
        consentMedicationRisks: consentState.medicationRisks,
        consentTermsAndPrivacy: consentState.termsAndPrivacy,
      },
      termsVersion: '2026-04-02',
      timestamp,
    };

    // Save to sessionStorage for checkout pickup
    sessionStorage.setItem('telehealthConsent', JSON.stringify(consentData));

    // Try API call (requires auth — works during checkout flow)
    try {
      await api.post('/checkout/consent', consentData);
    } catch {
      // If not logged in, consent saved to sessionStorage for checkout
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  const allChecked = consentState?.allChecked ?? false;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <img loading="lazy" src="/oval-logo.webp" alt="Oval Care" className={styles.logo} />
        <h1 className={styles.title}>Electronic Patient Consent Form</h1>
        <p className={styles.subtitle}>Telehealth Services &amp; Compounded Medication Authorization</p>

        <section className={styles.section}>
          <h2>1. Purpose of This Consent</h2>
          <p>This electronic consent form authorizes MDProactive LLC dba WebDoctors (&ldquo;Provider&rdquo;), the telemedicine provider for this consultation, to:</p>
          <ul>
            <li>Deliver medical care through telemedicine.</li>
            <li>Evaluate and/or treat conditions related to weight loss, metabolic health, and longevity.</li>
            <li>Discuss, recommend, or prescribe compounded medications when medically appropriate.</li>
            <li>Capture, store, and retain this electronically signed consent in your EHR.</li>
            <li>Generate a PDF version of this consent and save it as part of your permanent patient record.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Section A &mdash; Telemedicine Consent</h2>

          <h3>1. Nature of Telemedicine</h3>
          <p>Telemedicine involves the use of electronic communication technologies, including video, audio, messaging, and digital records, to provide medical evaluation, diagnosis, treatment, follow-up, and patient education.</p>
          <p>I understand that:</p>
          <ul>
            <li>Telemedicine allows me to receive healthcare remotely.</li>
            <li>The Provider will assess my condition based on the medical history, symptoms, and information I provide.</li>
            <li>Telemedicine may limit the Provider&rsquo;s ability to perform a hands-on physical examination.</li>
          </ul>

          <h3>2. Benefits of Telemedicine</h3>
          <p>I understand that the potential benefits include:</p>
          <ul>
            <li>Increased access to medical care.</li>
            <li>Convenience and reduced travel.</li>
            <li>Faster access to consultations, prescriptions, and refills.</li>
          </ul>

          <h3>3. Risks &amp; Limitations of Telemedicine</h3>
          <p>I understand that telemedicine has potential risks, including but not limited to:</p>
          <ul>
            <li>Technical failures, poor transmission, or incomplete data.</li>
            <li>Delays in evaluation due to technical issues.</li>
            <li>The possibility that a remote Provider may determine an in-person examination is required.</li>
            <li>Security protocols are in place, but the privacy of electronic information cannot be guaranteed.</li>
          </ul>
          <p>I acknowledge that no warranty or guarantee has been made regarding the expected results of telemedicine care.</p>

          <h3>4. Privacy &amp; Confidentiality</h3>
          <p>I understand that:</p>
          <ul>
            <li>My medical information will be kept confidential as required under HIPAA.</li>
            <li>Electronic communication will be encrypted or secured when possible.</li>
            <li>I am responsible for ensuring privacy on my end during the telemedicine visit.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Section B &mdash; Consent for Compounded Medication Treatments</h2>

          <h3>1. Voluntary Request for Consultation</h3>
          <p>I am requesting this consultation voluntarily after performing my own independent research and due diligence through various resources, including information available on the Internet. I confirm that no person or organization has pressured, persuaded, or influenced me to seek this consultation or purchase any product.</p>

          <h3>2. Understanding Non-FDA-Approved Products</h3>
          <p>I understand that certain medications, peptides, hormones, supplements, or therapies discussed or prescribed during this consultation may:</p>
          <ul>
            <li>Not be approved by the U.S. Food and Drug Administration (FDA).</li>
            <li>Be prepared by a licensed compounding pharmacy.</li>
            <li>Differ from standard pharmaceutical products in potency, formulation, or appearance.</li>
          </ul>
          <p>The Provider has informed me of the FDA status of all compounded products discussed.</p>

          <h3>3. Risks, Side Effects &amp; Unknown Long-Term Outcomes</h3>
          <p>I understand that compounded medications may be generally considered safe, but long-term risks may be unknown, including risks related to:</p>
          <ul>
            <li>Cancer</li>
            <li>Blood clots or vascular events</li>
            <li>Cardiovascular complications</li>
            <li>Neurological conditions</li>
            <li>Unknown drug interactions or long-term biological effects</li>
          </ul>
          <p>I accept these uncertainties and am choosing to proceed voluntarily.</p>

          <h3>4. Use of Licensed Compounding Pharmacies</h3>
          <p>I understand that:</p>
          <ul>
            <li>Compounded medications may not be available at standard retail pharmacies.</li>
            <li>These products are dispensed exclusively by licensed compounding pharmacies.</li>
            <li>Such pharmacies follow regulatory standards but do not undergo the same FDA approval processes as commercial pharmaceutical manufacturers.</li>
          </ul>

          <h3>5. Voluntary Use &amp; Personal Responsibility</h3>
          <p>I acknowledge that:</p>
          <ul>
            <li>I am voluntarily electing to use these products.</li>
            <li>I have reviewed available information and conducted my own research.</li>
            <li>I assume full responsibility for my decision to proceed.</li>
          </ul>

          <h3>6. When to Seek Immediate Medical Attention</h3>
          <p>I agree to discontinue use immediately and seek urgent medical care at my own expense if I experience:</p>
          <ul>
            <li>Sudden or severe headache</li>
            <li>Chest pain or shortness of breath</li>
            <li>Blurred vision</li>
            <li>Numbness or weakness in any part of the body</li>
            <li>Severe abdominal pain</li>
            <li>Difficulty speaking, dizziness, or fainting</li>
            <li>Allergic reactions such as swelling, hives, or difficulty breathing</li>
          </ul>

          <h3>7. Provider&rsquo;s Medical Judgment</h3>
          <p>I understand that:</p>
          <ul>
            <li>The Provider may approve or decline requested medications based solely on medical judgment.</li>
            <li>A request for a specific product (e.g., semaglutide, tirzepatide, peptides, hormone therapy) does not guarantee a prescription.</li>
            <li>Treatment decisions are based on safety, clinical guidelines, and my health history.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Section C &mdash; Electronic Consent, Storage &amp; E-Signature</h2>
          <p>By electronically signing this form:</p>
          <ul>
            <li>I consent to receiving telemedicine services from Provider.</li>
            <li>I authorize Provider to capture my consent electronically.</li>
            <li>I authorize Provider to save this consent as a PDF in EHR as a permanent part of my medical record.</li>
            <li>I acknowledge that my electronic signature is legally binding and equivalent to a handwritten signature.</li>
            <li>I understand that submitting this form electronically finalizes my consent for this and future related telemedicine visits unless revoked.</li>
          </ul>
        </section>

        <section className={styles.consentSection}>
          <h2>Acknowledgment &amp; Consent</h2>
          <p>Please confirm each statement below to proceed:</p>
          <ConsentDisclaimer onChange={handleConsentChange} />
        </section>

        <div className={styles.submitSection}>
          {submitted ? (
            <div className={styles.confirmation}>
              <p>Your consent has been recorded successfully. You may close this page or continue with your consultation.</p>
            </div>
          ) : (
            <button
              className={styles.submitButton}
              disabled={!allChecked || submitting}
              onClick={handleSubmit}
              type="button"
            >
              {submitting ? 'Submitting...' : 'I Consent \u2014 Continue'}
            </button>
          )}
        </div>

        <div className={styles.footer}>
          <p>By checking the consent boxes and clicking the button above, you acknowledge that you have read, understood, and agree to all terms outlined in this consent form.</p>
        </div>
      </div>
    </div>
  );
};

export default TelehealthConsentPage;
